import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';

import { AnalyticsAction } from '../../../../services/analytics.constants';
import { getSearchOrigin } from '../../../../store/analytics.selectors';
import * as ZenitContextHelpers from '../../../helpers/context.helper';
import { ZenitSelectItem, ZenitSetOriginItemList, ZenitSetOriginItemListError } from '../../actions/zenit.actions';
import { getIndexCalculationValues } from '../../selectors/custom.selectors';
import { getZenitOriginItemEventFrom, getZenitOriginItemListName } from '../../selectors/zenit.selectors';

import { Product } from '@api/catalog/products/products.models';
import { getPage } from '@core/infrastructure/state/core.selectors';
import { getSearchType } from '@features/search/infrastructure/state/selectors/search.selectors';
import { GiftCardSelectors } from '@pages/gift-card/store/gift-card.selector';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { GA4AnalyticsPublicConstants } from '@shared/analytics/GA4/public.api';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';
import * as AppEventsAnalyticsActions from '@shared/analytics/store/app-events-analytics.actions';
import { GoToProduct } from '@shared/navigation/store/navigation.actions';
import { getActiveModalData } from '@ui/dialog/store/modal.selectors';

@Injectable()
export class ClickOriginEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
    ) { }

    /** Efectos para tracking de origen de listas de items */

    public SetProductSourceListTracker$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitSelectItem),
            concatLatestFrom(() => [
                this.store.select(getPage),
                this.store.select(getActiveModalData),
                this.store.select(getZenitOriginItemListName),
                this.store.select(getZenitOriginItemEventFrom),
                this.store.select(getIndexCalculationValues),
            ]),
            map(([
                action,
                page,
                modalData,
                originItemListName,
                sourceEventFromItem,
                indexCalculationValues,
            ]) => {
                try {
                    const originList = ZenitContextHelpers.getList(
                        action?.eventFrom,
                        page,
                        modalData,
                        action?.item,
                        undefined,
                        {
                            originItemListName,
                            place: action?.place,
                            searchOrigin: action?.searchOrigin,
                            sourceEventFromItem,
                        }
                    );
                    const originIndex = ZenitContextHelpers.getProductItemIndex(
                        action?.item,
                        action?.eventFrom,
                        {
                            ...indexCalculationValues,
                            sourceEventFromItem,
                        },
                        { place: action?.place, relatedsSectionId: action?.relatedsSectionId }
                    );
                    return ZenitSetOriginItemList({
                        originItemEventFrom: action.eventFrom,
                        originItemListIndex: originIndex,
                        originItemListName: originList,
                        originItemRelatedType: action?.relatedsSectionId ? action?.eventFrom : undefined,
                    });
                } catch (error) {
                    sendObservabilityLog(error, 'SetProductSourceListTracker$ ZenitSetOriginItemList');
                    return ZenitSetOriginItemListError({ error });
                }
            })
        );
    });

    public GetProductSourceListTracker$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                AppEventsAnalyticsActions.BundleClickAnalytics,
                AppEventsAnalyticsActions.ClickProductoBuscadorAnalytics,
                AppEventsAnalyticsActions.ProductClickAnalytics,
                AppEventsAnalyticsActions.RelatedProductClickAnalytics,
                AppEventsAnalyticsActions.ControlCartAnalytics
            ),
            // Este map es necesario porque los modelos de datos de estas acciones no están estandarizados, una vez se elimine GA3 se simplificarán.
            map((actionData) => ({ ...actionData, ...(actionData as any).data })),
            filter((actionData) => {
                return !actionData?.action
                    || [
                        AnalyticsAction.ProductClick,
                        AnalyticsAction.verVistaRapidaProducto,
                    ].includes(actionData?.action);
            }),
            concatLatestFrom(() => [
                this.store.select(getSearchOrigin),
                this.store.select(getSearchType),
            ]),
            map(([action, searchOrigin, searchType]) => {
                const product = action?.product;
                const category = action?.category;

                const eventFrom: EventFrom = GA4AnalyticsPublicConstants.AnalitycsCartCategoryToEventFrom[category]
                    || action?.eventFrom || action?.parentType || EventFrom.search;

                return ZenitSelectItem({
                    eventFrom,
                    item: product,
                    place: action?.place,
                    relatedsSectionId: action?.relatedsSectionId,
                    searchOrigin: searchOrigin?.origin || searchType,
                });
            })
        );
    });

    public GetProductSourceListBundles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToProduct),
            filter((action) => action?.fromBundle),
            map(() => ZenitSelectItem({
                eventFrom: EventFrom.fichaBundle,
            }))
        );
    });

    public GetProductSourceListGiftcardTracker$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.ClickOnCmsComponentAnalytics),
            map((actionData) => {
                if (/(virtualGiftCard)|(\/\w{2}\/gift-card\/virtual)/.test(actionData?.linkSrc)) return 'Virtual Card';
                if (/(physicalGiftCard)|(\/\w{2}\/gift-card\/physical)/.test(actionData?.linkSrc)) return 'Physical Card';
                return undefined;
            }),
            filter((productType) => !!productType),
            concatLatestFrom(() => this.store.select(GiftCardSelectors.getBothGiftCards)),
            map(([productType, giftCards]) => giftCards.find((giftCard) => giftCard.productType === productType)),
            filter((giftCardData) => !!giftCardData),
            map((giftCardData) => ZenitSelectItem({
                eventFrom: EventFrom.giftcard,
                item: { ...giftCardData } as unknown as Product,
            }))
        );
    });
}
